import type {
  SupportedChainName,
  TokenDeployment,
  TokenInfo,
} from "@src/components/DefuseSDK/types/base"
import { findTokenByAddress } from "../../utils/paymentUri/findTokenByAddress"
import {
  type ParsedAmount,
  parsePaymentUri,
} from "../../utils/paymentUri/parsePaymentUri"
import { getScannableChains } from "../../utils/scanner/getScannableChains"
import { isBaseToken, isUnifiedToken } from "../../utils/token"
import { findTokenBySymbol } from "../../utils/tokenUrlSymbol"
import { classifyScannedAddress } from "../contacts/utils/classifyScannedAddress"
import { detectNetworkFromAddress } from "../contacts/utils/detectNetworkFromAddress"
import { stripPaymentUriPrefix } from "../contacts/utils/stripPaymentUriPrefix"
import { isAmbiguousAddress } from "./isAmbiguousAddress"

export type ScanResolution = {
  address: string
  chain: SupportedChainName
  token: TokenInfo | null
}

const SCHEME_PREFIX_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:/

export type ResolveScanInput = {
  rawText: string
  tokenList: TokenInfo[]
  /**
   * The token currently in scope on the home screen (from
   * `useSearchParams().get("token")`). Used by the default-token rule.
   */
  currentTokenSymbol: string | null
}

export type ResolveScanOutput =
  | {
      kind: "pay"
      recipient: string
      chain: SupportedChainName
      token: TokenInfo
      amount:
        | { kind: "decimal"; value: string }
        | { kind: "rawTokenUnits"; value: string }
    }
  | { kind: "askAction"; resolution: ScanResolution }
  | {
      kind: "reject"
      reason:
        | "unrecognized"
        | "recognized-unsupported"
        | "ambiguous"
        | "wc"
        | "evm-unsupported"
      message: string
    }

export function resolveScan(input: ResolveScanInput): ResolveScanOutput {
  const parsed = parsePaymentUri(input.rawText)

  if (parsed.kind === "rejected") {
    return resolveRejected(input, parsed.reason)
  }

  const payment = parsed.payment

  // A scheme prefix (e.g. `bitcoin:`, `ethereum:`, `solana:`) is a strong
  // pay-intent signal: the user told us which chain. Skip the ambiguity check
  // in that case — only run it on bare addresses where chain inference is the
  // only signal we have.
  const trimmed = input.rawText.trim()
  const hasSchemePrefix = SCHEME_PREFIX_RE.test(trimmed)

  if (!hasSchemePrefix && isAmbiguousAddress(payment.recipient)) {
    return {
      kind: "reject",
      reason: "ambiguous",
      message:
        "Couldn't determine the network from this QR. To save it as a contact, add it manually.",
    }
  }

  if (hasPositiveAmount(payment.amount)) {
    const resolvedToken = findTokenByAddress({
      chain: payment.chain,
      tokenAddress: payment.tokenAddress,
      tokenList: input.tokenList,
    })
    if (!resolvedToken) {
      return {
        kind: "reject",
        reason: "unrecognized",
        message: "This token isn't supported on near.com yet.",
      }
    }
    return {
      kind: "pay",
      recipient: payment.recipient,
      chain: payment.chain,
      token: resolvedToken,
      amount: payment.amount,
    }
  }

  // Amount absent / zero — fall to askAction. Thread the QR's token address
  // through so a token-specific URI (e.g. an ERC-20 transfer without a
  // positive amount) keeps its token instead of falling to the default rule.
  //
  // A bare EVM `0x…` address resolves a default token like any other chain
  // (`parsePaymentUri` pins it to `eth`). `resolveDefaultToken` only keeps the
  // home-screen token when it actually deploys on that chain, so an
  // incompatible token (e.g. Zcash-only ZEC) can never pair with an EVM
  // recipient — it falls back to native ETH.
  return resolveAskAction({
    input,
    address: payment.recipient,
    chain: payment.chain,
    tokenAddress: payment.tokenAddress,
  })
}

function hasPositiveAmount(
  amount: ParsedAmount | null | undefined
): amount is ParsedAmount {
  if (!amount) return false
  // Strict digit-string check — robust against malformed URIs (rejects hex,
  // scientific notation, negatives, NaN) and dodges Number()'s imprecision.
  // Positive iff at least one non-zero digit is present.
  const validShape =
    amount.kind === "decimal"
      ? /^[0-9]+(\.[0-9]+)?$/.test(amount.value)
      : /^[0-9]+$/.test(amount.value)
  return validShape && /[1-9]/.test(amount.value)
}

function resolveRejected(
  input: ResolveScanInput,
  reason: "wc_not_supported" | "evm_unsupported_chain" | "unsupported_format"
): ResolveScanOutput {
  if (reason === "wc_not_supported") {
    return {
      kind: "reject",
      reason: "wc",
      message: "WalletConnect QRs aren't supported here yet.",
    }
  }
  if (reason === "evm_unsupported_chain") {
    return {
      kind: "reject",
      reason: "evm-unsupported",
      message: "This QR is for an unsupported EVM network.",
    }
  }
  return resolveUnsupportedFormat(input)
}

function resolveUnsupportedFormat(input: ResolveScanInput): ResolveScanOutput {
  const normalized = stripPaymentUriPrefix(input.rawText).trim()
  const availableChains = getScannableChains()
  const classification = classifyScannedAddress(normalized, availableChains)

  if (classification.kind === "recognized-unsupported") {
    return {
      kind: "reject",
      reason: "recognized-unsupported",
      message: `Unfortunately, we do not currently support ${classification.formatName} addresses.`,
    }
  }

  if (classification.kind === "supported") {
    if (isAmbiguousAddress(normalized)) {
      return {
        kind: "reject",
        reason: "ambiguous",
        message:
          "Couldn't determine the network from this QR. To save it as a contact, add it manually.",
      }
    }
    const detected = detectNetworkFromAddress(normalized)
    if (detected == null) {
      return {
        kind: "reject",
        reason: "ambiguous",
        message:
          "Couldn't determine the network from this QR. To save it as a contact, add it manually.",
      }
    }
    // This branch only fires for the `parsePaymentUri` *rejected* path. A
    // bare EVM `0x…` address is accepted by the parser (its
    // `detectChainFromAddress` recognizes it), so it never reaches here —
    // anything that does is non-EVM and resolves a token normally.
    return resolveAskAction({
      input,
      address: normalized,
      chain: detected,
    })
  }

  return {
    kind: "reject",
    reason: "unrecognized",
    message: "Couldn't read a recognizable address from this QR.",
  }
}

function resolveAskAction(args: {
  input: ResolveScanInput
  address: string
  chain: SupportedChainName
  /**
   * Token contract address from the scanned URI, if any. When present and it
   * resolves to a supported token, that token is preferred over the
   * default-token rule — the QR explicitly named which token to send.
   */
  tokenAddress?: string | null
}): ResolveScanOutput {
  const token =
    (args.tokenAddress != null
      ? findTokenByAddress({
          chain: args.chain,
          tokenAddress: args.tokenAddress,
          tokenList: args.input.tokenList,
        })
      : null) ??
    resolveDefaultToken(
      args.input.tokenList,
      args.input.currentTokenSymbol,
      args.chain
    )
  return {
    kind: "askAction",
    resolution: {
      address: args.address,
      chain: args.chain,
      token,
    },
  }
}

function resolveDefaultToken(
  tokenList: TokenInfo[],
  currentTokenSymbol: string | null,
  chain: SupportedChainName
): TokenInfo | null {
  if (currentTokenSymbol) {
    const current = findTokenBySymbol(currentTokenSymbol, tokenList)
    if (current && tokenSupportsChain(current, chain)) return current
  }
  return findDefaultSendTokenForChain(chain, tokenList)
}

function findDefaultSendTokenForChain(
  chain: SupportedChainName,
  tokenList: TokenInfo[]
): TokenInfo | null {
  // Prefer unified tokens so the pay URL stays chain-agnostic (`token=ETH`
  // vs `token=ETH:eth`), matching the historical `findTokenBySymbol` order.
  for (const token of tokenList) {
    if (
      isUnifiedToken(token) &&
      token.groupedTokens.some((t) =>
        t.deployments.some((d) => isDefaultSendDeploymentOnChain(d, chain))
      )
    ) {
      return token
    }
  }
  for (const token of tokenList) {
    if (
      isBaseToken(token) &&
      token.deployments.some((d) => isDefaultSendDeploymentOnChain(d, chain))
    ) {
      return token
    }
  }
  return null
}

function isDefaultSendDeploymentOnChain(
  deployment: TokenDeployment,
  chain: SupportedChainName
): boolean {
  if (deployment.chainName !== chain) return false

  return (
    ("type" in deployment && deployment.type === "native") ||
    (chain === "near" &&
      "address" in deployment &&
      deployment.address === "wrap.near")
  )
}

function tokenSupportsChain(
  token: TokenInfo,
  chain: SupportedChainName
): boolean {
  if (isUnifiedToken(token)) {
    return token.groupedTokens.some((t) =>
      t.deployments.some((d) => d.chainName === chain)
    )
  }
  if (isBaseToken(token)) {
    return token.deployments.some((d) => d.chainName === chain)
  }
  return false
}
